/**
 * Thin, typed client for /api/v1. Handles the envelope, CSRF double-submit, idempotency keys,
 * optimistic-concurrency tokens and a single-flight, cross-tab-safe token refresh.
 */
import type { Meta } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string | undefined;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {},
              requestId?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Field-level messages for inline form validation. */
  get fields(): Record<string, string> {
    const f = this.details.fields;
    return f && typeof f === "object" ? (f as Record<string, string>) : {};
  }
}

const BASE = "/api/v1";
const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const NO_REFRESH = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout", "/auth/csrf"];

export interface RequestOptions {
  body?: unknown;
  form?: FormData;
  query?: Record<string, string | number | boolean | null | undefined>;
  idempotencyKey?: string;
  ifMatch?: number;
  signal?: AbortSignal;
}

export interface Envelope<T> {
  data: T;
  meta: Meta;
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

let csrfPromise: Promise<string> | null = null;
async function csrfToken(): Promise<string> {
  const existing = readCookie("csrf_token");
  if (existing) return decodeURIComponent(existing);
  csrfPromise ??= fetch(`${BASE}/auth/csrf`, { credentials: "same-origin" })
    .then((r) => r.json())
    .then((body: Envelope<{ csrf_token: string }>) => body.data.csrf_token)
    .finally(() => {
      csrfPromise = null;
    });
  return csrfPromise;
}

let refreshing: Promise<boolean> | null = null;
/** Refresh the session once, even if many requests (or tabs) hit 401 at the same time. */
export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  const run = async (): Promise<boolean> => {
    const resp = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": await csrfToken() },
    });
    return resp.ok;
  };
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  refreshing = (locks ? locks.request("finance-auth-refresh", run) : run()).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export function newIdempotencyKey(): string {
  return `web-${crypto.randomUUID()}`;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return `${BASE}${path}${qs ? `?${qs}` : ""}`;
}

async function send(method: string, path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (UNSAFE.has(method)) headers["X-CSRF-Token"] = await csrfToken();
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
  if (opts.ifMatch !== undefined) headers["If-Match"] = String(opts.ifMatch);
  return fetch(buildUrl(path, opts.query), {
    method,
    headers,
    credentials: "same-origin",
    body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
    signal: opts.signal,
  });
}

async function toError(resp: Response): Promise<ApiError> {
  try {
    const body = (await resp.json()) as {
      error?: { code: string; message: string; details?: Record<string, unknown> };
      meta?: Meta;
    };
    if (body.error) {
      return new ApiError(resp.status, body.error.code, body.error.message, body.error.details ?? {},
                          body.meta?.request_id);
    }
  } catch {
    /* non-JSON error (proxy, network) */
  }
  return new ApiError(resp.status, "HTTP_ERROR", resp.statusText || "Request failed");
}

export async function request<T>(method: string, path: string,
                                 opts: RequestOptions = {}): Promise<Envelope<T>> {
  let resp: Response;
  try {
    resp = await send(method, path, opts);
    if (resp.status === 401 && !NO_REFRESH.some((p) => path.startsWith(p))) {
      if (await refreshSession()) resp = await send(method, path, opts);
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "NETWORK_ERROR", "Can't reach the server. Check your connection.");
  }
  if (!resp.ok) throw await toError(resp);
  if (resp.status === 204) return { data: undefined as T, meta: { request_id: "" } };
  return (await resp.json()) as Envelope<T>;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<T>("GET", path, { query, signal }).then((r) => r.data),
  getPage: <T>(path: string, query?: RequestOptions["query"]) => request<T>("GET", path, { query }),
  post: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, "body"> = {}) =>
    request<T>("POST", path, { ...opts, body }).then((r) => r.data),
  postEnvelope: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, "body"> = {}) =>
    request<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, { body }).then((r) => r.data),
  patch: <T>(path: string, body: unknown, version: number) =>
    request<T>("PATCH", path, { body, ifMatch: version }).then((r) => r.data),
  /** For resources without optimistic concurrency (e.g. import review rows). */
  patchUnversioned: <T>(path: string, body: unknown) =>
    request<T>("PATCH", path, { body }).then((r) => r.data),
  delete: (path: string) => request<void>("DELETE", path).then(() => undefined),
  upload: <T>(path: string, form: FormData) =>
    request<T>("POST", path, { form }).then((r) => r.data),
};
